/**
 * Routing.
 *
 * The legacy app had no router: `showView(name)` toggled a `hidden` class on one
 * of ~30 `#view-*` divs, all of which lived in the DOM at once, and the "current
 * view" was mirrored into sessionStorage so a refresh could restore it. Real
 * routes replace all of that — the URL is the state, and deep links work.
 *
 * Route paths intentionally mirror the legacy view names (`patient-appointments`
 * -> `/patient/appointments`) so old bookmarks map predictably.
 */

import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { RequireAuth, RequireRole } from './guards';
import { ROLES } from '../context/AuthContext';

import { LoginPage } from '../features/auth/LoginPage';
import { RegisterPage } from '../features/auth/RegisterPage';

import { PatientHome } from '../features/patient/PatientHome';
import { PatientDoctors } from '../features/patient/PatientDoctors';
import { PatientBooking } from '../features/patient/PatientBooking';
import { PatientAppointments } from '../features/patient/PatientAppointments';
import { PatientPrescriptions } from '../features/patient/PatientPrescriptions';
import { PatientMedications } from '../features/patient/PatientMedications';
import { PatientMedicalCV } from '../features/patient/PatientMedicalCV';
import { PatientTransactions } from '../features/patient/PatientTransactions';

import { DoctorOverview } from '../features/doctor/DoctorOverview';
import { LiveView } from '../features/doctor/live/LiveView';
import { DoctorClinics } from '../features/doctor/DoctorClinics';
import { DoctorPrescriptions } from '../features/doctor/DoctorPrescriptions';
import { DoctorOffers } from '../features/doctor/DoctorOffers';
import { DoctorVouchers } from '../features/doctor/DoctorVouchers';
import { DoctorPackages } from '../features/doctor/DoctorPackages';
import { PhotographyBooking } from '../features/doctor/PhotographyBooking';

import { ServiceBookingPage } from '../features/patient/services/ServiceBookingPage';

import { HospitalStaff } from '../features/hospital/HospitalStaff';
import { ReceptionDesk } from '../features/reception/ReceptionDesk';
import { OperationsBoard } from '../features/operations/OperationsBoard';
import { SecretaryQueue } from '../features/secretary/SecretaryQueue';

import { InstitutionOverview } from '../features/institution/InstitutionOverview';
import { InstitutionServices } from '../features/institution/InstitutionServices';
import { InstitutionBookings } from '../features/institution/InstitutionBookings';
import { InstitutionMembers } from '../features/institution/InstitutionMembers';
import { InstitutionFinance } from '../features/institution/InstitutionFinance';

import { AdminDoctors } from '../features/admin/AdminDoctors';
import { AdminRegistrations } from '../features/admin/AdminRegistrations';
import { AdminAppointments } from '../features/admin/AdminAppointments';
import { AdminPackages } from '../features/admin/AdminPackages';
import { AdminPackageRequests } from '../features/admin/AdminPackageRequests';
import { AdminPhotographyTeams } from '../features/admin/AdminPhotographyTeams';
import { AdminInstitutions } from '../features/admin/AdminInstitutions';
import { AdminTeam } from '../features/admin/AdminTeam';
import { AdminAccounts } from '../features/admin/AdminAccounts';
import { AdminAiAccess } from '../features/admin/AdminAiAccess';

import { TeamTasks } from '../features/team/TeamTasks';
import { SettingsPage } from '../features/settings/SettingsPage';
import { NotFoundPage } from '../features/misc/NotFoundPage';
import { LandingRedirect } from './guards';

export function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      {/* Authenticated shell */}
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {/* Patient — team members share these screens, since they hold a patient account */}
        <Route
          path="/patient"
          element={<RequireRole roles={[ROLES.PATIENT, ROLES.TEAM_MEMBER, ROLES.ADMIN]} />}
        >
          <Route index element={<PatientHome />} />
          <Route path="doctors" element={<PatientDoctors />} />
          <Route path="doctors/:categoryId" element={<PatientDoctors />} />
          <Route path="book/:doctorId" element={<PatientBooking />} />
          {/* Booking an institution service — a surgery, a scan, a bed.
              Same calendar idiom as a consultation, different subject. */}
          <Route path="service/:serviceId" element={<ServiceBookingPage />} />
          <Route path="appointments" element={<PatientAppointments />} />
          <Route path="prescriptions" element={<PatientPrescriptions />} />
          <Route path="medications" element={<PatientMedications />} />
          <Route path="medical-cv" element={<PatientMedicalCV />} />
          <Route path="transactions" element={<PatientTransactions />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Doctor */}
        <Route path="/doctor" element={<RequireRole roles={[ROLES.DOCTOR]} />}>
          <Route index element={<DoctorOverview />} />
          {/* The schedule was merged into the clinics screen, where the legacy
              build had it. Kept as a redirect so existing links and bookmarks
              land somewhere sensible rather than on the not-found page. */}
          <Route path="schedule" element={<Navigate to="/doctor/clinics" replace />} />
          <Route path="live" element={<LiveView />} />
          <Route path="clinics" element={<DoctorClinics />} />
          <Route path="prescriptions" element={<DoctorPrescriptions />} />
          <Route path="offers" element={<DoctorOffers />} />
          <Route path="vouchers" element={<DoctorVouchers />} />
          <Route path="packages" element={<DoctorPackages />} />
          <Route path="photography" element={<PhotographyBooking />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Admin */}
        <Route path="/admin" element={<RequireRole roles={[ROLES.ADMIN]} />}>
          <Route index element={<AdminDoctors />} />
          <Route path="registrations" element={<AdminRegistrations />} />
          <Route path="appointments" element={<AdminAppointments />} />
          <Route path="packages" element={<AdminPackages />} />
          <Route path="requests" element={<AdminPackageRequests />} />
          <Route path="institutions" element={<AdminInstitutions />} />
          <Route path="photography" element={<AdminPhotographyTeams />} />
          <Route path="team" element={<AdminTeam />} />
          <Route path="accounts" element={<AdminAccounts />} />
          <Route path="ai" element={<AdminAiAccess />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Institutions — a hospital, polyclinic or lab centre manager.
            Admins are admitted too so a problem can be reproduced from the
            manager's own screens rather than described over the phone. */}
        <Route
          path="/institution"
          element={<RequireRole roles={[ROLES.INSTITUTION, ROLES.ADMIN]} />}
        >
          <Route index element={<InstitutionOverview />} />
          <Route path="services" element={<InstitutionServices />} />
          <Route path="bookings" element={<InstitutionBookings />} />
          <Route path="members" element={<InstitutionMembers />} />
          <Route path="finance" element={<InstitutionFinance />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/*
          The four hospital staff areas.

          Each admits the institution manager and the platform admin alongside
          its own role, for the reason the institution branch already states:
          a problem is better reproduced from the operator's own screens than
          described over the phone. A co-admin reaches /hospital because that is
          their home, but what they may actually *do* there is decided by the
          capability check inside the screen and re-checked on every request —
          being admitted to a route is not being permitted an action.
        */}
        <Route
          path="/hospital"
          element={<RequireRole roles={[ROLES.CO_ADMIN, ROLES.INSTITUTION, ROLES.ADMIN]} />}
        >
          <Route index element={<HospitalStaff />} />
          <Route path="staff" element={<HospitalStaff />} />
          <Route path="operations" element={<OperationsBoard />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        <Route
          path="/reception"
          element={
            <RequireRole
              roles={[ROLES.RECEPTIONIST, ROLES.CO_ADMIN, ROLES.INSTITUTION, ROLES.ADMIN]}
            />
          }
        >
          <Route index element={<ReceptionDesk />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        <Route
          path="/operations"
          element={
            <RequireRole
              roles={[ROLES.OPERATIONS_MANAGER, ROLES.CO_ADMIN, ROLES.INSTITUTION, ROLES.ADMIN]}
            />
          }
        >
          <Route index element={<OperationsBoard />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        <Route
          path="/secretary"
          element={<RequireRole roles={[ROLES.SECRETARY, ROLES.INSTITUTION, ROLES.ADMIN]} />}
        >
          <Route index element={<SecretaryQueue />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Marketing team */}
        <Route path="/team" element={<RequireRole roles={[ROLES.TEAM_MEMBER, ROLES.ADMIN]} />}>
          <Route index element={<TeamTasks />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
      </Route>

      {/* Send "/" to whichever home the session's role owns */}
      <Route path="/" element={<LandingRedirect />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default AppRoutes;
